// Renders the floor plan (tables, zones, labels) as SVG. Coordinate space is
// data.width x data.height (default 1080x1920 == the portrait board).
export const PLAN_W = 1080;
export const PLAN_H = 1920;

export const TABLE_STYLES = {
  oval: 'Oval with rail',
  classic: 'Classic rectangle',
  modern: 'Modern pill',
  wood: 'Wood rail casino',
};

const Frame = ({ t, stroke, width, dash, rx, fill }) => (
  <rect x={t.x - t.w / 2} y={t.y - t.h / 2} width={t.w} height={t.h} rx={rx}
    fill={fill} stroke={stroke} strokeWidth={width} strokeDasharray={dash} />
);

const Shape = ({ t, style, fill, stroke, width, dash }) => {
  const stadium = Math.min(t.w, t.h) / 2;
  if (style === 'classic') {
    return <Frame t={t} rx={5} fill={fill} stroke={stroke} width={width} dash={dash} />;
  }
  if (style === 'modern') {
    return (
      <>
        <Frame t={t} rx={stadium} fill="#111" stroke={stroke} width={width} dash={dash} />
        <rect x={t.x - t.w / 2 + 4} y={t.y - t.h / 2 + 4} width={t.w - 8} height={t.h - 8} rx={Math.max(0, stadium - 4)} fill={fill} />
      </>
    );
  }
  if (style === 'wood') {
    const rail = 8;
    return (
      <>
        <Frame t={t} rx={stadium} fill="#8b5a2b" stroke={stroke} width={width} dash={dash} />
        <rect x={t.x - t.w / 2 + 3} y={t.y - t.h / 2 + 3} width={t.w - 6} height={t.h - 6} rx={Math.max(0, stadium - 3)} fill="none" stroke="#c89b5a" strokeWidth={1.5} />
        <rect x={t.x - t.w / 2 + rail} y={t.y - t.h / 2 + rail} width={t.w - rail * 2} height={t.h - rail * 2} rx={Math.max(0, stadium - rail)} fill={fill} stroke="rgba(0,0,0,0.4)" strokeWidth={1} />
      </>
    );
  }
  // oval (default): dark padded rail, felt, betting line
  const rail = 7;
  const ix = Math.max(0, stadium - rail);
  return (
    <>
      <Frame t={t} rx={stadium} fill="#2a1a12" stroke={stroke} width={width} dash={dash} />
      <rect x={t.x - t.w / 2 + rail} y={t.y - t.h / 2 + rail} width={t.w - rail * 2} height={t.h - rail * 2} rx={ix} fill={fill} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />
      <rect x={t.x - t.w / 2 + rail + 6} y={t.y - t.h / 2 + rail + 6} width={Math.max(0, t.w - rail * 2 - 12)} height={Math.max(0, t.h - rail * 2 - 12)} rx={Math.max(0, ix - 6)} fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth={1} />
    </>
  );
};

export const Table = ({ t, onClick, selected, showSeats = true, tableStyle = 'oval' }) => {
  const fill = t.color || '#ffffff';
  const textColor = '#111';  // table numbers are always black
  const stroke = selected ? '#facc15' : t.conflict ? '#ff3b3b' : t.manual ? '#facc15' : '#111';
  const width = selected || t.conflict ? 4 : t.manual ? 2.5 : 1.5;
  const dash = t.conflict && !selected ? '6 4' : undefined;
  return (
    <g
      transform={t.rot ? `rotate(${t.rot} ${t.x} ${t.y})` : undefined}
      onClick={onClick ? () => onClick(t) : undefined}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      data-testid={`floor-table-${t.num}`}
    >
      <Shape t={t} style={tableStyle} fill={fill} stroke={stroke} width={width} dash={dash} />
      {t.label
        ? <text x={t.x} y={t.y + 7} textAnchor="middle" fontSize={20} fontWeight={700} fill={textColor} fontFamily="Arial, sans-serif" letterSpacing={1}>{t.label}</text>
        : <text x={t.x} y={t.y + (showSeats && t.filled != null ? -4 : 9)} textAnchor="middle" fontSize={26} fontWeight={700} fill={textColor} fontFamily="Arial, sans-serif">{t.num}</text>}
      {showSeats && t.filled != null && (
        <text x={t.x} y={t.y + 22} textAnchor="middle" fontSize={19} fontWeight={700} fill={textColor} fontFamily="Arial, sans-serif">{`${t.filled}/${t.seats}`}</text>
      )}
    </g>
  );
};

// Bounding box (as an SVG viewBox string) of the plan elements whose centre y
// falls in [yMin, yMax). Used to split the tall plan into blocks for landscape.
export const cropViewBox = (data, yMin, yMax, pad = 40) => {
  const pts = [];
  (data.tables || []).forEach(t => { if (t.y >= yMin && t.y < yMax) pts.push([t.x - t.w / 2 - 12, t.y - t.h / 2 - 12], [t.x + t.w / 2 + 12, t.y + t.h / 2 + 12]); });
  (data.zones || []).forEach(z => { if (z.y >= yMin && z.y < yMax) pts.push([z.x - 120, z.y - 40], [z.x + 120, z.y + 10]); });
  (data.labels || []).forEach(l => { if (l.y >= yMin && l.y < yMax) pts.push([l.x - l.w / 2, l.y - l.h / 2], [l.x + l.w / 2, l.y + l.h / 2]); });
  if (pts.length === 0) return null;
  const xs = pts.map(p => p[0]); const ys = pts.map(p => p[1]);
  const x0 = Math.min(...xs) - pad; const y0 = Math.min(...ys) - pad;
  return `${x0} ${y0} ${Math.max(...xs) + pad - x0} ${Math.max(...ys) + pad - y0}`;
};

export const panelRect = (vb) => {
  if (!vb) return null;
  const [x, y, w, h] = vb.split(' ').map(Number);
  return { x, y, w, h };
};

export const FloorPlanSvg = ({ data, onTableClick, selectedNum, showSeats = true, className, style, preserveAspectRatio = 'xMidYMin meet', viewBox, panels = [] }) => {
  if (!data) return null;
  return (
    <svg viewBox={viewBox || `0 0 ${data.width || PLAN_W} ${data.height || PLAN_H}`} preserveAspectRatio={preserveAspectRatio} className={className} style={style} data-testid="floor-plan-svg">
      {panels.map((p, i) => (
        <rect key={`${p.x}-${p.y}`} x={p.x} y={p.y} width={p.w} height={p.h} rx={18} fill="rgba(8,4,4,0.65)" data-testid={`floor-panel-${i}`} />
      ))}
      {data.image_url && <image href={data.image_url} x={0} y={0} width={data.width || PLAN_W} height={data.height || PLAN_H} preserveAspectRatio="xMidYMid meet" opacity={0.95} />}
      {(data.zones || []).map(z => (
        <text key={z.name + z.x} x={z.x} y={z.y} textAnchor="middle" fontSize={36} fontWeight={700} fill="#fff" stroke="rgba(0,0,0,0.7)" strokeWidth={1} fontFamily="Arial, sans-serif" letterSpacing={1} style={{ paintOrder: 'stroke' }}>{z.name}</text>
      ))}
      {(data.labels || []).map(l => (
        <g key={l.text}>
          {l.shape === 'oval'
            ? <ellipse cx={l.x} cy={l.y} rx={l.w / 2} ry={l.h / 2} fill="#fff" stroke="#222" strokeWidth={2} />
            : <rect x={l.x - l.w / 2} y={l.y - l.h / 2} width={l.w} height={l.h} rx={8} fill={l.shape === 'dark' ? '#111' : '#fff'} stroke="#222" strokeWidth={2} />}
          <text x={l.x} y={l.y + 7} textAnchor="middle" fontSize={20} fontWeight={700} fill={l.shape === 'dark' ? '#fff' : '#111'} fontFamily="Arial, sans-serif">{l.text}</text>
        </g>
      ))}
      {(data.tables || []).map(t => (
        <Table key={t.num} t={t} onClick={onTableClick} selected={selectedNum === t.num} showSeats={showSeats} tableStyle={data.table_style || 'oval'} />
      ))}
    </svg>
  );
};

// Five arrow shapes the admin can pick from for the legend (70x34 viewBox).
export const ARROW_STYLES = {
  curved: { label: 'Curved swoosh', d: 'M2 30 C 14 8, 40 4, 54 10 L 52 2 L 68 14 L 50 24 L 52 16 C 38 12, 20 16, 6 32 Z' },
  straight: { label: 'Straight', d: 'M2 12 L 46 12 L 46 3 L 68 17 L 46 31 L 46 22 L 2 22 Z' },
  chevron: { label: 'Chevron', d: 'M4 3 L 40 3 L 68 17 L 40 31 L 4 31 L 30 17 Z' },
  bold: { label: 'Bold swoosh', d: 'M2 32 C 10 6, 36 0, 52 8 L 50 1 L 68 12 L 52 26 L 52 18 C 36 14, 18 20, 10 34 Z' },
  double: { label: 'Double arrow', d: 'M2 12 L 22 12 L 22 4 L 36 17 L 22 30 L 22 22 L 2 22 Z M 36 12 L 50 12 L 50 4 L 68 17 L 50 30 L 50 22 L 36 22 Z' },
};

export const ArrowIcon = ({ style = 'curved', color, width = 70, height = 34 }) => (
  <svg width={width} height={height} viewBox="0 0 70 34" style={{ flexShrink: 0 }}>
    <path d={(ARROW_STYLES[style] || ARROW_STYLES.curved).d} fill={color} stroke="rgba(0,0,0,0.5)" strokeWidth={1.5} />
  </svg>
);

// Legend of the day's events with a colored arrow each.
export const FloorLegend = ({ events, compact = false, large = false, dense = false, fillHeight = false, arrowStyle = 'curved', columns = 1, palette, onColorPick, onShapePick, showCounts = false }) => (
  <div data-testid="floor-legend" style={columns > 1
    ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: compact ? '6px 18px' : '10px 24px' }
    : { display: 'flex', flexDirection: 'column', gap: large ? (dense ? 8 : 26) : compact ? 6 : 10, ...(fillHeight ? { height: '100%', justifyContent: 'space-between' } : {}) }}>
    {events.map(e => (
      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }} data-testid={`floor-legend-${e.id}`}>
        <ArrowIcon style={e.arrow || arrowStyle} color={e.color} width={large ? (dense ? 72 : 96) : compact ? 54 : 70} height={large ? (dense ? 34 : 46) : compact ? 26 : 34} />
        <div style={{ lineHeight: 1.15, fontFamily: 'Arial, sans-serif', flex: 1, textShadow: large ? '0 2px 6px rgba(0,0,0,0.9)' : 'none' }}>
          <div style={{ fontSize: large ? (dense ? 22 : 26) : compact ? 15 : 20, fontWeight: 700, color: '#facc15', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: dense ? 'nowrap' : 'normal' }}>
            {e.time}{e.event_number && e.event_number !== '0' ? ` — Event #${e.event_number}` : ''}
            {showCounts && e.tables_count > 0 && <span style={{ color: '#fff', fontWeight: 600, marginLeft: 8, fontSize: compact ? 12 : 15 }}>{e.tables_count} tbl · {e.players} pl</span>}
            {e.conflicts && e.conflicts.length > 0 && (
              <span data-testid={`floor-conflict-${e.id}`} style={{ color: '#ff3b3b', fontWeight: 700, marginLeft: 8, fontSize: compact ? 11 : 13 }}>
                DUPLICATE TBL {e.conflicts.join(', ')}
              </span>
            )}
          </div>
          <div style={dense
            ? { fontSize: 20, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }
            : { fontSize: large ? 24 : compact ? 13 : 18, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.03em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.event_name}</div>
          {onColorPick && palette && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }} data-testid={`floor-color-picker-${e.id}`}>
              {palette.map(c => (
                <button key={c} onClick={() => onColorPick(e, c)} title={c}
                  data-testid={`floor-color-${e.id}-${c.replace('#', '')}`}
                  style={{ width: 18, height: 18, borderRadius: 4, background: c, border: e.color === c ? '2px solid #fff' : '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', padding: 0 }} />
              ))}
              {onShapePick && Object.keys(ARROW_STYLES).map(k => (
                <button key={k} onClick={() => onShapePick(e, k)} title={ARROW_STYLES[k].label}
                  data-testid={`floor-shape-${e.id}-${k}`}
                  style={{ height: 18, borderRadius: 4, background: 'rgba(255,255,255,0.08)', border: (e.arrow || arrowStyle) === k ? '2px solid #fff' : '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}>
                  <ArrowIcon style={k} color={e.color} width={30} height={14} />
                </button>
              ))}
              {e.custom_color && (
                <button onClick={() => onColorPick(e, null)} data-testid={`floor-color-reset-${e.id}`}
                  style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, padding: '0 6px', cursor: 'pointer' }}>default</button>
              )}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);
